import type { InferType } from 'yup';
import { array, boolean, date, number, object, string } from 'yup';

export enum OrderStatus {
  success = 'Выполнен',
  pending = 'В обработке',
}

export const addressSchema = object()
  .shape({
    city: string().required(),
    comment: string().defined().nullable(),
    entrance: string().defined().nullable(),
    flat: string().defined().nullable(),
    floor: string().defined().nullable(),
    house: string().required(),
    id: number().required(),
    intercom: string().defined().nullable(),
    street: string().required(),
    zip: string().defined().nullable(),
  })
  .required();

export const userSchema = object()
  .shape({
    id: number().nullable(),
    name: string().defined().nullable(),
    lastName: string().defined().nullable(),
    phone: string().defined(),
    email: string().defined().nullable(),
    birthDate: date().nullable(),
    age: number().nullable(),
    isPhoneConfirmed: boolean().required(),
    isSister: boolean().nullable(),
  })
  .required();

const historyProductType = object()
  .shape({
    id: number().required(),
    oldPrice: number().required().nullable(),
    price: number().required(),
    productId: number().required(),
    skuId: number().required(),
    quantity: number().required(),
    isAvailable: boolean().required(),
    skuName: string().required(),
    skuCode: string().nullable(),
  })
  .required();

const orderHistoryItemSchema = object()
  .shape({
    id: number().required(),
    price: number().required(),
    dateCreate: string().required(),
    isCancelable: boolean().required(),
    gift: boolean().required(),
    status: string<OrderStatus>().required(),
    isPaid: boolean().required(),
    paymentType: string().required(),
    basket: object()
      .shape({ items: array().ensure().of(historyProductType).required() })
      .required(),
  })
  .required();

const userResponseSchema = object().shape({
  user: userSchema,
  orders: array().ensure().of(orderHistoryItemSchema).required(),
  addresses: array().ensure().of(addressSchema).required(),
  productOrderedIds: array().ensure().of(number().required()).required(),
  subscriptions: array()
    .ensure()
    .of(
      object()
        .shape({
          active: boolean().required(),
          code: string().required(),
          name: string().required(),
        })
        .required(),
    )
    .required(),
});

const updateUserBodySchemas = object().shape({
  name: string().optional(),
  lastName: string().optional(),
  phone: string().optional(),
  email: string().email().optional(),
  password: string().optional().min(8),
  isSister: boolean().optional(),
});

const checkPhoneCodeBodySchema = object().shape({ code: string().required() });

const checkPhoneCodeResponseSchema = object().shape({ codeValid: boolean().required() }).required();

export const houseAddressSchema = object()
  .shape({
    street: string().required(),
    house: string().required(),
  })
  .required();

export const createAddressBodySchema = houseAddressSchema.shape({
  locationCode: string().required(),
  flat: string().required(),
  zip: string().defined(),
  intercom: string().defined(),
  floor: string().defined(),
  entrance: string().defined(),
  comment: string().defined(),
});

const createAddressResponseSchema = array().ensure().of(addressSchema).required();

const addressParamsSchema = object().shape({ id: number().positive().required() }).required();

const updateSubscriptionsBodySchema = object()
  .shape({ subscriptions: array().of(string().required()).required() })
  .required();

const updateSisterBodySchema = object().shape({ sister: boolean() }).required();

const cancelOrderBodySchema = object()
  .shape({
    orderId: number().required(),
  })
  .required();

export type UserType = InferType<typeof userResponseSchema>['user'];

export type ProfileDataType = Omit<InferType<typeof userResponseSchema>, 'user'>;

export type UserAddressType = InferType<typeof addressSchema>;

export type HistoryProductType = InferType<typeof historyProductType>;

export type OrderHistoryItemType = InferType<typeof orderHistoryItemSchema>;

export const UserAPI = {
  details: StrictFetch.prepare({
    url: 'profile',
    schemas: { response: userResponseSchema },
    options: { methodKey: 'user:details' },
  }),
  update: StrictFetch.prepare({
    url: 'profile',
    method: HTTPMethod.POST,
    schemas: { response: userResponseSchema, body: updateUserBodySchemas },
  }),
  confirmPhone: StrictFetch.prepare({
    url: 'code/send',
  }),
  checkPhoneCode: StrictFetch.prepare({
    url: 'code/confirm',
    method: HTTPMethod.POST,
    schemas: {
      response: checkPhoneCodeResponseSchema,
      body: checkPhoneCodeBodySchema,
    },
  }),
  createAddress: StrictFetch.prepare({
    url: 'address',
    method: HTTPMethod.POST,
    schemas: {
      body: createAddressBodySchema,
      response: createAddressResponseSchema,
    },
  }),
  updateAddress: StrictFetch.prepare({
    url: ({ id }) => `address/${id}`,
    method: HTTPMethod.POST,
    schemas: {
      body: createAddressBodySchema,
      params: addressParamsSchema,
      response: createAddressResponseSchema,
    },
  }),
  deleteAddress: StrictFetch.prepare({
    url: ({ id }) => `address/${id}`,
    method: HTTPMethod.DELETE,
    schemas: {
      response: createAddressResponseSchema,
      params: addressParamsSchema,
    },
  }),
  updateSubscriptions: StrictFetch.prepare({
    url: 'subscriptions',
    method: HTTPMethod.POST,
    schemas: { body: updateSubscriptionsBodySchema },
    options: {
      methodKey: 'profile:update_subscriptions',
      selfInterrupted: true,
    },
  }),
  updateSister: StrictFetch.prepare({
    url: 'sister',
    method: HTTPMethod.POST,
    schemas: { body: updateSisterBodySchema },
  }),
  cancelOrder: StrictFetch.prepare({
    url: 'orders/cancel',
    method: HTTPMethod.PUT,
    schemas: { body: cancelOrderBodySchema, response: userResponseSchema },
  }),
};
